import {
  CollectionStateType,
  ItemsType,
  mapCmsItemsById,
} from '@packages/systems/core/records/CollectionStateType';
import type {ItemRecordType} from '@packages/systems/core/records/ItemRecord';
import type {
  DesignerStores,
  SharedStores,
} from '@packages/systems/designer/flux';
import {selectCurrentLocale} from '@packages/systems/localization';
import type {
  Locale,
  LocalizationState,
} from '@packages/systems/localization-types';
import {memoize, singleMemo, weakMemo} from '@packages/utilities/memo';
import {List, Map} from 'immutable';
import {createSelector} from 'reselect';
import {CurrentCollectionStoreProps} from '@packages/systems/cms/types';
import {selectCurrentPage} from '@packages/utilities/platform-selectors';

export const selectDatabaseId = (state: DesignerStores) =>
  state.SiteDataStore.database._id;

const _getAllCMSItemsFromStore = weakMemo(
  (collectionState: Partial<CollectionStateType>) =>
    memoize((locales: Locale[] | null): List<ItemRecordType> => {
      const {items: itemsFromCollection, localizedItems} = collectionState;

      if (
        !locales ||
        !Array.isArray(locales) ||
        // This condition is important, because it allows to correctly handle a case,
        //   when we pass the result of `selectAllLocales(…)` selector as `locales`
        //   here, which is an empty array for a site without localization.
        (Array.isArray(locales) && !locales.length)
      ) {
        return itemsFromCollection?.toList() || List<ItemRecordType>();
      }

      return locales.reduce((items, locale) => {
        const localizedItemsForLocale = localizedItems?.get(locale.cmsId ?? '');
        if (!localizedItemsForLocale) {
          return items;
        }

        return items.push(...localizedItemsForLocale.toArray());
      }, List<ItemRecordType>());
    })
);

/** The *recommended* way to obtain CMS items. They are for a current locale only
 *  work OK for both localized and non-localized sites.
 *
 *  The returned items are in a shape of "Item ID -> Item" mapping, which makes
 *  them usually usable in both cases where either the mapping or just a flat
 *  list is needed (because both `Map` and `List` are coming from Immutable.js,
 *  therefore we can use `.find`, `.filter` etc. on them in the same way).
 */
export const selectCmsItemsForCurrentLocale = createSelector(
  (state: {CollectionStore: CollectionStateType}) => state.CollectionStore,
  (state: {LocalizationStore: LocalizationState}) =>
    selectCurrentLocale(state.LocalizationStore),
  (collectionStore, currentLocale) => {
    return mapCmsItemsById(
      _getAllCMSItemsFromStore(collectionStore)(
        currentLocale ? [currentLocale] : null
      )
    );
  }
);

/** The *discouraged* way to obtain CMS items. This works only for sites
 *  without localization and for the localized ones will return an
 *  empty collection.
 *
 *  We still use this selector only because removing it is a non-trivial
 *  chunk of work yet to be tackled.
 *
 *  The returned items are in a shape of "Item ID -> Item" mapping, which makes
 *  them usually usable in both cases where either the mapping or just a flat
 *  list is needed (because both `Map` and `List` are coming from Immutable.js,
 *  therefore we can use `.find`, `.filter` etc. on them in the same way).
 */
// Appears to be memoized:
// eslint-disable-next-line webflow/selector-memoization
export const selectCmsItemsIgnoringLocalization = (
  collectionState: Partial<CollectionStateType>
): ItemsType => {
  return mapCmsItemsById(_getAllCMSItemsFromStore(collectionState)(null));
};

/** The *low-level* way to obtain CMS items, for a given list of locales.
 *
 *  Warning: The empty list of locales (or `null`) makes this selector
 *  return items only if the sites has no localization set up.
 *
 *  The returned items are in a shape of a list. This means we have to
 *  wrap the results with `mapCmsItemsById` in a call site, if needed.
 *
 *  We use this selector in places where:
 *  - we pass multiple locales as a param, therefore we cannot map items
 *    by their IDs, as it would happen for `selectCmsItemsForCurrentLocale`
 *  - we are afraid of bringing an entire `collectionState` as the param
 *    (e.g. to not mess up the existing memoization by passing
 *    `collectionState` through a chain of components' hierarchy), therefore
 *    we cannot use `selectCmsItemsForCurrentLocale`.
 */
// Not being used as a UI selector, and appears to be memoized:
// eslint-disable-next-line webflow/selector-memoization
export const selectCmsItemsForLocales = (
  collectionState: Partial<CollectionStateType>,
  locales: Locale[] | null
): List<ItemRecordType> => {
  return _getAllCMSItemsFromStore(collectionState)(locales);
};

const _getCMSItemsFromStore = weakMemo(
  (collectionState: Partial<CollectionStateType>) =>
    /**
     * NOTE: We use `memoize` here instead of `weakMemo` because weakMemo uses referential integrity,
     * and `memoize` does a deep compare. This lets us have multiple callsites that pass in the same
     * itemIDs and still get a cache hit.
     */
    memoize((itemIds: string[]) =>
      singleMemo((locale?: Locale | null): List<ItemRecordType> => {
        const {items: itemsFromCollection, localizedItems} = collectionState;

        return itemIds.reduce((items, itemId) => {
          if (!itemId) {
            return items;
          }

          const item =
            localizedItems?.getIn([locale?.cmsId, itemId]) ??
            itemsFromCollection?.get(itemId);

          if (!item) {
            return items;
          }
          return items.push(item);
        }, List<ItemRecordType>());
      })
    )
);

/**
 * Deprecated: Use `selectCmsItemsForCurrentLocale` instead.
 */
export const __DEPRECATED__getCMSItemsFromStore = (
  collectionState: Partial<CollectionStateType>,
  itemIds: string[],
  locale?: Locale | null
) => {
  return _getCMSItemsFromStore(collectionState)(itemIds)(locale);
};

/**
 * This is an easy helper to select a single item from the store.
 */
export const getCMSItemFromStore = (
  collectionState: Partial<CollectionStateType>,
  itemId: string,
  locale?: Locale | null
): ItemRecordType | undefined => {
  const foundItems = _getCMSItemsFromStore(collectionState)([itemId])(locale);
  return foundItems.first();
};

export const selectIsCurrentItemInline = createSelector(
  (state: {CurrentCollectionStore: CurrentCollectionStoreProps}) =>
    state.CurrentCollectionStore,
  (currentCollectionState) => {
    if (!currentCollectionState) {
      return false;
    }

    const {currentInlineItem} = currentCollectionState;
    return Boolean(currentInlineItem && currentInlineItem.get('item'));
  }
);

export const selectCurrentCollectionFromItem = createSelector(
  (state: {CurrentCollectionStore: CurrentCollectionStoreProps}) =>
    state.CurrentCollectionStore,
  selectIsCurrentItemInline,
  (currentCollectionState, isCurrentItemInline) => {
    const currentCollection = isCurrentItemInline
      ? currentCollectionState?.currentInlineItem?.collection
      : currentCollectionState?.currentCollection;

    return currentCollection ?? null;
  }
);

export const selectCurrentItemFromCurrentCollectionStore = createSelector(
  (state: {CurrentCollectionStore: CurrentCollectionStoreProps}) =>
    state.CurrentCollectionStore,
  selectIsCurrentItemInline,
  (currentCollectionState, isCurrentItemInline) => {
    const currentItem = isCurrentItemInline
      ? currentCollectionState?.currentInlineItem?.item
      : currentCollectionState?.currentItem;

    return currentItem ?? null;
  }
);

// TODO: Cleanup the duplicate selectors in this file, like selectCurrentCollection, selectCurrentItemFromCurrentCollection, etc.

export const selectAllCollections = createSelector(
  (state: {CollectionStore: CollectionStateType}) => state.CollectionStore,
  (collectionState) => collectionState?.collections ?? Map()
);

// Maps pageId -> collection name for CMS template pages (dynamic pages with dynCollection)
export const selectPageCollectionNameMap = createSelector(
  (state: DesignerStores) => state.PageStore.get('dynamicPages'),
  (state: DesignerStores) => state.CollectionStore.collections,
  (dynamicPages, collections): Record<string, string> => {
    const map: Record<string, string> = {};
    dynamicPages?.forEach((page) => {
      const dynCollection = page.get('dynCollection');
      if (dynCollection) {
        const collection = collections.get(dynCollection);
        if (collection) {
          map[page.get('id')] = collection.name;
        }
      }
    });
    return map;
  }
);

/**
 * Returns true if the site has any CMS collections.
 */
export const selectIsDynamicSite = createSelector(
  selectAllCollections,
  (collections) => collections.size > 0
);

export const selectCurrentCollection = (
  state: Pick<SharedStores, 'CurrentCollectionStore'>
) => state.CurrentCollectionStore.currentCollection;

export const selectCurrentItemFromCurrentCollection = (
  state: Pick<SharedStores, 'CurrentCollectionStore'>
) => state.CurrentCollectionStore.currentItem;

export const selectCurrentItemFieldFromCurrentCollection = (
  state: Pick<SharedStores, 'CurrentCollectionStore'>
) => state.CurrentCollectionStore.currentField;

export const selectCollectionState = (
  state: Pick<SharedStores, 'CollectionStore'>
) => state.CollectionStore;

export const selectIsVariantsSavingInProgress = (
  state: Pick<SharedStores, 'DataManagerUIStore'>
) => state.DataManagerUIStore.variantsSavingInProgress;

export const selectAutosaveData = (state: DesignerStores) =>
  state.CurrentCollectionStore.autosaveData;

export const selectLastCreatedItemId = (state: DesignerStores) =>
  state.CurrentCollectionStore.lastCreatedItemId;

export const selectCurrentPageCollectionId = createSelector(
  (state: DesignerStores) => state.PageStore.dynamicPages,
  (state) => state.DesignerStore.currentPageId,
  (dynamicPages, currentPageId) => {
    const currentPage = dynamicPages.get(currentPageId);
    if (!currentPage) {
      return null;
    }
    return currentPage.get('dynCollection');
  }
);

export const selectTranslationsLoading = (state: DesignerStores) => {
  return state.CurrentCollectionStore.translationsLoading;
};

export const selectIsCurrentCMSItemJitTranslationPending = createSelector(
  selectCurrentCollectionFromItem,
  selectCurrentItemFromCurrentCollectionStore,
  (state: DesignerStores) =>
    state.CurrentCollectionStore.jitTranslationsPending,
  (state: DesignerStores) => selectCurrentLocale(state.LocalizationStore),
  (currentCollection, currentItem, jitTranslationsPending, currentLocale) => {
    const collectionId = currentCollection?._id;
    const itemId = currentItem?.get('_id');
    const cmsLocaleId = currentLocale?.cmsId;

    return Boolean(
      collectionId &&
      itemId &&
      cmsLocaleId &&
      jitTranslationsPending.getIn([collectionId, itemId, cmsLocaleId])
    );
  }
);

export const selectItemHasPendingTranslations = createSelector(
  selectCurrentItemFromCurrentCollectionStore,
  (state: DesignerStores) => state.CurrentCollectionStore.translationsLoading,
  (currentItem, translationsLoading) => {
    if (!currentItem) return false;

    const translationsLoadingForItem = translationsLoading.get(
      currentItem.get('_id')
    );
    const hasPendingItemTranslations = translationsLoadingForItem
      ?.valueSeq()
      ?.some((isLoading) => isLoading);
    const hasPendingFieldTranslations = currentItem.get('_translating');
    return hasPendingItemTranslations || hasPendingFieldTranslations;
  }
);

export const selectSiteShortNameFromSharedStores = (state: SharedStores) =>
  state.SiteDataStore.shortName;

export const selectIsCMSImportPending = (state: DesignerStores) =>
  state.DesignerStore?.csvImportPending ?? false;

export const selectIsSampleDataGenerationInProgress = (state: SharedStores) =>
  state.DataManagerUIStore.sampleDataGenerationInProgress;

/**
 * Selects data needed to derive the current collection item field id.
 * Returns an array of strings: [collectionId, itemId, fieldName] or null.
 */
export const selectCurrentCollectionItemFieldIdPath = createSelector(
  selectCurrentItemFromCurrentCollectionStore,
  selectCurrentItemFieldFromCurrentCollection,
  (state: DesignerStores) => state.ContentEditorStore.item,
  (state: DesignerStores) => state.ContentEditorStore.field,
  (state: DesignerStores) => state.FormUIStore.get('focusedField'),
  (
    collectionItem,
    collectionField,
    contentEditorItem,
    contentEditorField,
    focusedField
  ): [string, string, string] | null => {
    if (!collectionItem && !contentEditorItem) {
      return null;
    }

    const item = collectionItem ?? contentEditorItem;
    const field = collectionField ?? contentEditorField;

    if (!item) {
      return null;
    }

    // Assign fieldName from item field if it exists, otherwise use focusedField keyPath (drop form id)
    const fieldName = field?.get('slug') ?? focusedField?.skip(1).join('.');
    const collectionId = item.get('_cid');
    const itemId = item.get('_id');

    const isNonEmptyString = (value: unknown): value is string =>
      typeof value === 'string' && value.length > 0;

    if (
      !isNonEmptyString(collectionId) ||
      !isNonEmptyString(itemId) ||
      !isNonEmptyString(fieldName)
    ) {
      return null;
    }

    return [collectionId, itemId, fieldName];
  }
);

export const selectCollectionDataById =
  (collectionId?: string | null) => (state: DesignerStores) => {
    if (!collectionId) {
      return null;
    }
    return state.CollectionStore.collections.get(collectionId);
  };

export const selectIsDataManagerModalOpen = (state: DesignerStores) => {
  return state.CurrentCollectionStore.showInDataManagerModal;
};

/**
 * Use this to select the current collection when not in the dynamic data workflow.
 */
// Safe since this doesn't return a new object:
// eslint-disable-next-line webflow/selector-memoization
export const selectCurrentCollectionFromPage = (state: DesignerStores) => {
  const currentPage = selectCurrentPage(state);
  const dynCollectionID = currentPage && currentPage.get('dynCollection');

  if (!dynCollectionID) {
    return null;
  }

  return selectCollectionDataById(dynCollectionID)(state);
};

/**
 * Use this to select the current item when not in the dynamic data workflow.
 */
export const selectCurrentItemFromPage = createSelector(
  (state: DesignerStores) => state.CollectionStore,
  (state: DesignerStores) => state.DesignerStore.currentPageItemId,
  (state: DesignerStores) => selectCurrentLocale(state.LocalizationStore),
  (collectionState, currentPageItemId, currentLocale) => {
    if (!currentPageItemId) {
      return null;
    }

    return getCMSItemFromStore(
      collectionState,
      currentPageItemId,
      currentLocale
    );
  }
);
